import { ShieldBan } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import ModalShell from "../modal/ModalShell";

/** At most this many characters in the reason (PrincipalBlock.ReasonMaxLength on the backend). */
const REASON_MAX_LENGTH = 500;

interface AdminBlockDialogProps {
  /** Who is blocked, as the Admin reads it ("Lucy Lyrebird"). */
  name: string;
  busy: boolean;
  onConfirm: (reason: string) => void;
  onClose: () => void;
}

/**
 * Asks the Admin for the reason before a block (US-111). The reason stays in the back office;
 * the blocked person only sees the contact line.
 */
const AdminBlockDialog = ({ name, busy, onConfirm, onClose }: AdminBlockDialogProps) => {
  const { t } = useTranslation();
  const [reason, setReason] = useState("");

  return (
    <ModalShell onClose={onClose} labelledBy="admin-block-title">
      <div className="p-6" data-testid="admin-block-dialog">
        <h3 id="admin-block-title" className="flex items-center gap-2 text-lg font-semibold text-grey-10 dark:text-grey-90">
          <ShieldBan size={20} aria-hidden="true" />
          {t("admin.blocks.title", { name })}
        </h3>
        <p className="mt-1 text-sm text-grey-40 dark:text-grey-80">{t("admin.blocks.body")}</p>
        <label className="mt-4 block text-sm font-medium text-grey-30 dark:text-grey-70">
          {t("admin.blocks.reasonLabel")}
          <textarea
            className="input mt-1 min-h-[80px]"
            value={reason}
            maxLength={REASON_MAX_LENGTH}
            onChange={(event) => setReason(event.target.value)}
            disabled={busy}
          />
        </label>
        <div className="mt-6 flex justify-end gap-2">
          <button type="button" className="btn btn-secondary" disabled={busy} onClick={onClose}>
            {t("common.cancel")}
          </button>
          <button
            type="button"
            className="btn btn-danger"
            disabled={busy || reason.trim() === ""}
            onClick={() => onConfirm(reason.trim())}
          >
            {t("admin.blocks.block")}
          </button>
        </div>
      </div>
    </ModalShell>
  );
};

export default AdminBlockDialog;
